import type { GencowProcedureDef, QueryDef } from "@gencow/core";
import { getQueryDef, getRegisteredMutations, getRegisteredQueries } from "@gencow/core";
import { collectProcedureRouteMaps } from "./procedure-route-maps.js";

type LegacyMutationLike = {
  name: string;
  isPublic?: boolean;
};

type ProcedureRouteMaps = {
  queryProcedureMap: Map<string, GencowProcedureDef<"query", any, any, any>>;
  mutationProcedureMap: Map<string, GencowProcedureDef<"mutation", any, any, any>>;
};

export type RuntimeOperationInventoryEntry = {
  key: string;
  allowAnonymous: boolean;
};

export type RuntimeOperationInventory = {
  legacyQueries: RuntimeOperationInventoryEntry[];
  legacyMutations: RuntimeOperationInventoryEntry[];
  procedureQueries: RuntimeOperationInventoryEntry[];
  procedureMutations: RuntimeOperationInventoryEntry[];
  totalQueries: number;
  totalMutations: number;
  totalOperations: number;
};

export function collectRuntimeOperationInventory(params: {
  appModule?: unknown;
  procedureMaps?: ProcedureRouteMaps;
  getRegisteredQueries?: () => string[];
  getQueryDef?: (name: string) => QueryDef | undefined;
  getRegisteredMutations?: () => LegacyMutationLike[];
  collectProcedureRouteMaps?: typeof collectProcedureRouteMaps;
} = {}): RuntimeOperationInventory {
  const registeredQueries = params.getRegisteredQueries ?? getRegisteredQueries;
  const queryDefFor = params.getQueryDef ?? getQueryDef;
  const registeredMutations = params.getRegisteredMutations ?? getRegisteredMutations;
  const procedureMaps =
    params.procedureMaps ??
    (params.collectProcedureRouteMaps ?? collectProcedureRouteMaps)(params.appModule);

  const legacyQueries = registeredQueries().map((key) => ({
    key,
    allowAnonymous: queryDefFor(key)?.isPublic === true,
  }));
  const legacyMutations = registeredMutations().map((mutation) => ({
    key: mutation.name,
    allowAnonymous: mutation.isPublic === true,
  }));
  const procedureQueries = [...procedureMaps.queryProcedureMap.entries()].map(([key, procedure]) => ({
    key,
    allowAnonymous: procedure.allowAnonymous === true,
  }));
  const procedureMutations = [...procedureMaps.mutationProcedureMap.entries()].map(
    ([key, procedure]) => ({
      key,
      allowAnonymous: procedure.allowAnonymous === true,
    }),
  );
  const totalQueries = legacyQueries.length + procedureQueries.length;
  const totalMutations = legacyMutations.length + procedureMutations.length;

  return {
    legacyQueries,
    legacyMutations,
    procedureQueries,
    procedureMutations,
    totalQueries,
    totalMutations,
    totalOperations: totalQueries + totalMutations,
  };
}
